import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { goTo } from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";

const LINKS = [
  ["services", "#services"],
  ["recovery", "#recovery"],
  ["about", "#about"],
  ["consult", "#consult"],
  ["faq", "#faq"],
];

const LANGS = [
  ["EN", "en"],
  ["VI", "vi"],
  ["KO", "ko"],
];

export const LangSwitch = ({ className = "", variant = "desktop" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-0.5 rounded-full border border-gold/25 p-0.5 ${className}`} data-testid={`lang-switch-${variant}`}>
      {LANGS.map(([label, code]) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          data-testid={`lang-switch-${variant}-${code}`}
          aria-pressed={lang === code}
          className={`rounded-full px-2.5 py-1 font-mono text-[10px] tracking-[0.12em] transition-[background-color,color] duration-300 ${
            lang === code ? "bg-gold font-semibold text-ink" : "text-cream/60 hover:text-gold"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

export default function Nav() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => (e) => {
    setOpen(false);
    goTo(e, hash);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled ? "glass-nav border-b border-gold/15 shadow-[0_10px_40px_rgba(0,0,0,0.35)]" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-3 sm:px-6 lg:px-12">
        <a href="#top" onClick={go("#top")} className="flex items-center gap-3" data-testid="nav-logo-link">
          <Logo className="h-11 w-11" />
          <span className="leading-tight">
            <span className="block font-display text-lg font-semibold tracking-[0.14em] text-cream">DUSTIN CAMPOS</span>
            <span className="block font-mono text-[9px] uppercase tracking-[0.28em] text-gold/80">
              {t.nav.license}
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {LINKS.map(([key, hash]) => (
            <a
              key={hash}
              href={hash}
              onClick={go(hash)}
              className="whitespace-nowrap text-[13px] font-medium uppercase tracking-[0.18em] text-cream/70 transition-colors duration-300 hover:text-gold"
            >
              {t.nav.links[key]}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="tel:+18085004711"
            data-testid="nav-phone-link"
            className="hidden items-center gap-2 whitespace-nowrap rounded-full border border-gold/30 px-4 py-2 text-sm text-cream/90 transition-colors duration-300 hover:border-gold/60 hover:text-gold md:flex"
          >
            <Phone className="h-4 w-4 text-gold" />
            808-500-4711
          </a>
          <LangSwitch className="hidden md:flex" />
          <a
            href="#consult"
            onClick={go("#consult")}
            data-testid="nav-book-cta"
            className="btn-gold hidden whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold sm:block"
          >
            {t.nav.book}
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-gold lg:hidden"
            aria-label={open ? t.nav.menuClose : t.nav.menuOpen}
            data-testid="nav-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="glass-nav border-b border-gold/15 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <nav className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map(([key, hash]) => (
                <a
                  key={hash}
                  href={hash}
                  onClick={go(hash)}
                  className="border-b border-cream/10 py-4 font-display text-3xl text-cream transition-colors duration-300 hover:text-gold"
                >
                  {t.nav.links[key]}
                </a>
              ))}
              <div className="flex flex-col gap-3 pt-5">
                <a href="#consult" onClick={go("#consult")} data-testid="nav-mobile-book-cta" className="btn-gold rounded-full py-3 text-center font-semibold">
                  {t.nav.book}
                </a>
                <a href="tel:+18085004711" data-testid="nav-mobile-phone-link" className="rounded-full border border-gold/30 py-3 text-center text-gold">
                  {t.nav.call}
                </a>
                <LangSwitch className="mt-2 flex justify-center" variant="mobile" />
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
